import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph, Skeleton, Tag } from '@digdir/designsystemet-react';
import type { TeamSurveys } from '../../../api/queries';
import { Routes } from '../../../enums/Routes';
import { DateUtils } from '../../../utils/DateUtils';
import { useGetLatestSurveyByTeamIdQuery } from '../../../hooks/queries/useGetLatestSurveyByTeamIdQuery';
import { SurveyDeadlineDialog } from '../../../components/SurveyDeadlineDialog';
import { formatScore, agreementTones } from '../../../components/AnalysisLayout/analysis.utils';
import {
  fromPsychologicalSafety,
  sortSurveys,
  type SurveyListItem,
} from '../../../domain/surveys/survey-list';
import { SurveyRowActions } from '../../SurveysPage/components/SurveyRowActions';
import { ConfirmDialog } from '../../SurveysPage/components/ConfirmDialog';
import { useSurveyActions } from '../../SurveysPage/useSurveyActions';
import classes from '../TeamDashboard.module.css';

const RECENT_COUNT = 3;

type OpenDialog = { kind: 'deadline' | 'delete'; item: SurveyListItem } | null;

const formatDate = (date: Date) => DateUtils.getShortDateFromDate(date, true);

type RecentSurveysProps = {
  teamId: number;
  surveys?: TeamSurveys[];
  isLoading: boolean;
  error: Error | null;
};

/** De siste målingene for psykologisk trygghet, med de samme handlingene som i undersøkelseslisten */
export const RecentSurveys = ({ teamId, surveys, isLoading, error }: RecentSurveysProps) => {
  const { t } = useTranslation();
  const { data: ongoing } = useGetLatestSurveyByTeamIdQuery(teamId);
  const actions = useSurveyActions(teamId);
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const closeDialog = () => setDialog(null);

  const ongoingLink = ongoing?.id && ongoing.uuid ? { id: ongoing.id, uuid: ongoing.uuid } : null;
  const items = sortSurveys(
    (surveys ?? []).map((survey) => fromPsychologicalSafety(survey, teamId, ongoingLink)),
  ).slice(0, RECENT_COUNT);

  const content = () => {
    if (isLoading) {
      return (
        <div aria-busy="true" className={classes.tablePadding}>
          <Skeleton variant="rectangle" width="100%" height={200} />
        </div>
      );
    }
    if (error) {
      return (
        <Alert data-color="danger">
          <Paragraph>{error.message}</Paragraph>
        </Alert>
      );
    }
    if (items.length === 0) {
      return (
        <div className={classes.emptyCard}>
          <Heading level={3} data-size="2xs">
            {t('dashboard.pastSurveys.noSurveys')}
          </Heading>
          <Paragraph data-size="sm" className={classes.subtle}>
            {t('dashboard.pastSurveys.noSurveysDescription')}
          </Paragraph>
          <div>
            <Button asChild variant="secondary" data-size="sm">
              <Link to={Routes.CreateSurvey}>{t('dashboard.pastSurveys.createSurvey')}</Link>
            </Button>
          </div>
        </div>
      );
    }
    return (
      <ul className={classes.surveyList}>
        {items.map((item) => (
          <li key={item.key} className={classes.surveyRow}>
            <div className={classes.surveyName}>
              <span className={classes.label}>{item.title}</span>
              <Paragraph data-size="sm" className={classes.subtle}>
                {item.deadline
                  ? t(item.isOngoing ? 'surveys.page.ongoingPeriod' : 'surveys.page.period', {
                      start: formatDate(item.startDate),
                      end: formatDate(item.deadline),
                    })
                  : formatDate(item.startDate)}
              </Paragraph>
            </div>
            <div className={classes.surveyResponses}>
              <Paragraph data-size="sm">
                {item.responses.count !== null && item.responses.expected
                  ? t('surveys.page.responses', {
                      count: item.responses.count,
                      expected: item.responses.expected,
                    })
                  : t('surveys.page.responseRate', { rate: Math.round(item.responses.rate) })}
              </Paragraph>
              <Tag data-size="sm" data-color={item.status.tone}>
                {item.status.label}
              </Tag>
            </div>
            <div className={classes.surveyResult}>
              {item.score && (
                <span className={classes.statValue}>
                  <span className={classes.surveyScore} data-tone={item.score.tone}>
                    {formatScore(item.score.value, 2)}
                  </span>
                  <span className={classes.subtle}>{t('analysis.scoreOf')}</span>
                </span>
              )}
              {item.agreement && (
                <Tag data-size="sm" data-color={agreementTones[item.agreement]}>
                  {t(`teamResult.agreement.${item.agreement}`)}
                </Tag>
              )}
              {item.note && <span className={classes.note}>{item.note}</span>}
            </div>
            <SurveyRowActions
              item={item}
              onCopyLink={actions.copyLink}
              onEditDeadline={(survey) => setDialog({ kind: 'deadline', item: survey })}
              onDelete={(survey) => setDialog({ kind: 'delete', item: survey })}
            />
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section aria-labelledby="surveys-heading" className={classes.section}>
      <div className={classes.sectionHeader}>
        <Heading id="surveys-heading" level={2} data-size="md">
          {t('dashboard.pastSurveys.title')}
        </Heading>
        <Link to={Routes.CurrentSurveys} className="ds-link" data-size="sm">
          {t('dashboard.pastSurveys.seeAll')}
        </Link>
      </div>
      <div className={classes.tableCard}>{content()}</div>

      {dialog?.kind === 'deadline' && (
        <SurveyDeadlineDialog
          open
          surveyTitle={dialog.item.title}
          createdAt={dialog.item.startDate}
          currentDeadlineInDays={dialog.item.deadlineInDays ?? 0}
          isSubmitting={actions.isUpdatingDeadline}
          onSubmit={(days) => actions.updateDeadline(dialog.item, days, closeDialog)}
          onClose={closeDialog}
        />
      )}
      {dialog?.kind === 'delete' && (
        <ConfirmDialog
          open
          title={t('surveys.page.deleteDialog.title', { title: dialog.item.title })}
          text={t('surveys.page.deleteDialog.text')}
          confirmLabel={t('surveys.page.deleteDialog.confirm')}
          dismissLabel={t('surveys.page.deleteDialog.dismiss')}
          isPending={actions.isDeleting}
          onConfirm={() => actions.remove(dialog.item, closeDialog)}
          onClose={closeDialog}
        />
      )}
    </section>
  );
};
